import { useEffect, useRef, useState } from "react";
import Cookies from "universal-cookie";
import { isEqual } from "lodash";
import Icon from "../../../Elements/Icon/Icon.tsx";
import SecurityFormComponent from "../UploadForms/SecurityFormComponent.js";
import useMediaQueries from "hooks/useMediaQueries.js";
import VerificationContainer from "../Modals/VerificationContainer.js";
import RemindToSaveSettingsModal from "../Modals/RemindToSaveSettingsModal.js";
import SubscribersFeaturesPromo from "./SubscribersFeaturesPromo.js";
import AnimationContainer from "components/Reusable/AnimationContainer/AnimationContainer.js";
import usePromoSubLogic from "../Helpers/usePromoSubLogic.js";
import UploaderSideBarOptions from "./UploaderSideBar/UploaderSideBarOptions.tsx";
import UploaderButtons from "./UploaderSideBar/UploaderButtons.tsx";
import CollapseButton from "./UploaderSideBar/CollapseButton.tsx";
import useHandleSubmit from "../hooks/useHandleSubmit.ts";
import Recaptcha from "components/Reusable/Recaptcha/Recaptcha.tsx";
import SideBarPromo from "./UploaderSideBar/SideBarPromo/SideBarPromo.tsx";
import { getAllowedVideoDuration } from "../Helpers/uploaderAPI.js";
import checkCurrDomainIsOrgDomain from "helpers/checkCurrDomainIsOrgDomain.ts";
import { SendToFormComponent } from "../UploadForms/SendToFormComponent.js";
import { PaymentFormComponent } from "../UploadForms/PaymentFormComponent.js";
import { SettingsFormComponent } from "../UploadForms/SettingsFormComponent.js";
import { useUploader } from "contexts/uploaderContext/uploaderContext.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import { useUser } from "contexts/userContext/userContext.js";
import { useToasts } from "react-toast-notifications";
import { useContent } from "contexts/contentContext/contentContext.js";

const cookies = new Cookies();

export const CloseIcon = ({ open, setCurrentOption }) => {
  const { mobileCondition } = useMediaQueries();
  return mobileCondition ? null : (
    <button
      className="close-section"
      onClick={() => {
        setCurrentOption(open ? "send-plane" : null);
      }}
    >
      <Icon name={"home/arrows"} darkGray />
    </button>
  );
};

export const UploaderSideBar = ({
  setCurrentRecipients,
  sideBarOption,
  setSideBarOption,
}) => {
  const {
    getUploading,
    setHideSubmit,
    getHideSubmit,
    getRecipientEmail,
    getIsVideodurationExceeded,
    setAllowedVideoDuration,
  } = useUploader();
  const { isLoggedIn, getUserData, getIsIsrael } = useUser();
  const { getHtmlByKey, getTextByKey } = useContent();
  const { setModal } = useModal();
  const { mobileCondition } = useMediaQueries();
  const { addToast } = useToasts();

  const [body, setBody] = useState({
    SendOwnerEmail: cookies.get("sender-email") || "",
    BlockShare: false,
    DisplayVideo: undefined,
    EnablePreview: false,
    LimitDownloads: 0,
    MailPassword: "",
    NotifyDownload: true,
    ReceiveCopy: true,
    PaymentAmount: "",
    PaymentEachDownload: false,
    PaymentUserEmail: "",
    PreviewAccess: false,
    RecipientAccess: false,
    SaveMail: undefined,
    SendToEmail: getRecipientEmail() ? [getRecipientEmail()] : [],
    ShareToEmailBodyTitle: undefined,
    ShareToEmailSubjectTitle: undefined,
  });

  const [currentOption, setCurrentOption] = useState(null);

  const getFormValuesRef = useRef(null);
  const recaptchaRef = useRef(null);

  // handle submit logic hook
  const { handleSubmit: submitFormAction, captchaRequired } = useHandleSubmit({
    formBody: body,
    setBody,
    getFormValuesRef,
    recaptchaRef,
    setCurrentOption,
    currentOption,
  });

  const handleSubmit = async () => {
    const formValuesRes = await getFormValuesRef.current();
    if (formValuesRes.succeed) {
      setBody((prev) => ({ ...prev, ...formValuesRes.values }));
      await submitFormAction({ ...body, ...formValuesRes.values });
    }
  };

  useEffect(() => {
    // setting email value to form cos its overriden by the multiple share input
    if (isLoggedIn()) {
      setBody((prev) => ({
        ...prev,
        SendOwnerEmail: getUserData().UserName,
        NotifyDownload: getUserData().UserReceiveDownloadNotification,
        ReceiveCopy: getUserData().UserReceiveMailCopy,
      }));
    }
  }, [isLoggedIn()]);

  // setSideBarOption - for handeling sidebar status outside of this component
  useEffect(() => {
    if (setSideBarOption) setSideBarOption(currentOption);
  }, [currentOption]);

  // sideBarOption - for handeling sidebar status outside of this component
  useEffect(() => {
    setCurrentOption(sideBarOption);
  }, [sideBarOption]);

  // setCurrentRecipients - to show the recipients in the mobile version
  useEffect(() => {
    if (setCurrentRecipients) setCurrentRecipients(body.SendToEmail);
  }, [body.SendToEmail]);

  useEffect(() => {
    if (!getUploading()) {
      setTimeout(() => {
        setCurrentOption("send-plane");
      }, 1000);
    }
  }, []);

  // logic of video duration exceeded
  useEffect(() => {
    if (getIsVideodurationExceeded()) {
      if (body.DisplayVideo) {
        addToast(
          getTextByKey("video-duration-exceeded").replace(
            "#number#",
            getIsVideodurationExceeded()
          ),
          {
            appearance: "error",
          }
        );
        setBody((prev) => ({
          ...prev,
          DisplayVideo: false,
        }));
        setCurrentOption("gear-light");
      }
    }
  }, [getIsVideodurationExceeded()]);

  useEffect(() => {
    if (isLoggedIn()) {
      getAllowedVideoDuration(setAllowedVideoDuration);
    } else {
      setAllowedVideoDuration(0);
    }
  }, [isLoggedIn()]);

  // detect when body state changed and  show/hide submit button
  const prevBody = useRef(body);
  const currentTime = useRef(Date.now());
  const callback = () => {
    if (!isEqual(prevBody.current, body)) {
      setHideSubmit(false);
    }
    prevBody.current = body;
  };

  const throttledCallback = () => {
    if (Date.now() - currentTime.current > 300) {
      callback();
      currentTime.current = Date.now();
    }
  };

  useEffect(() => {
    if (getUploading()) throttledCallback();
  }, [body]);

  useEffect(() => {
    if (getUploading() && !getHideSubmit()) {
      setModal({
        modal: RemindToSaveSettingsModal,
        container: VerificationContainer,
      });
    }
  }, [getHideSubmit()]);

  const swapFormFunction = async (key) => {
    if (getFormValuesRef.current && currentOption !== "send-plane") {
      const res = await getFormValuesRef.current();
      if (res.succeed) {
        setBody((prev) => ({ ...prev, ...res.values }));
        setCurrentOption((prev) => {
          // if (prev === key && !mobileCondition) return null;
          return key;
        });
      }
    } else {
      setCurrentOption(key);
    }
  };

  const optionsObj = {
    "send-plane": (
      <SendToFormComponent
        {...{
          body,
          setBody,
          getFormValuesRef,
          handleSubmit,
        }}
      />
    ),
    "gear-light": (
      <SettingsFormComponent
        {...{
          body,
          setBody,
          getFormValuesRef,
          handleSubmit,
        }}
      />
    ),
    "lock-alt-light": (
      <SecurityFormComponent
        {...{
          body,
          setBody,
          getFormValuesRef,
          handleSubmit,
        }}
      />
    ),
    "pay-light": (
      <PaymentFormComponent
        {...{
          body,
          setBody,
          getFormValuesRef,
          handleSubmit,
        }}
      />
    ),
  };

  const sideOptions = Object.keys(optionsObj);

  const hideJumbopay = !getIsIsrael() || getUserData().HideJumboPay;

  const { templateIdToShow, showPromo, setShowPromo, availableTemplatesIds } =
    usePromoSubLogic();


  // cos of animation delay we need to add this class to the promo container
  const [promoClassName, setPromoClassName] = useState(false);

  useEffect(() => {
    if (showPromo) {
      setPromoClassName(true);
    } else {
      setTimeout(() => {
        setPromoClassName(false);
      }, 500);
    }
  }, [showPromo]);

  // if (checkCurrDomainIsOrgDomain()) {
  //   return null;
  // }04195245acf347c2cc5c07c84fbc851615

  return (
    <div
      className={`home-upload-sidebar${currentOption ? "" : " collapsed"}${
        showPromo || promoClassName ? " promo-view" : ""
      }`}
    >
      {/* desktop sidebar collapsed section */}
      {!mobileCondition ? (
        !currentOption ? (
          <div className="sidebar-сollapsed">
            <CollapseButton setCurrentOption={setCurrentOption} collapsed />
            <UploaderButtons
              {...{
                body,
                sideOptions,
                swapFormFunction,
                currentOption,
                hideJumbopay,
                collapsed: true,
                handleSubmit,
              }}
            />
          </div>
        ) : (
          <CollapseButton setCurrentOption={setCurrentOption} />
        )
      ) : null}

      {/* promo sidebar section */}
      <AnimationContainer
        animationClassName="fade-in"
        animationOn={showPromo && templateIdToShow !== null}
      >
        <SubscribersFeaturesPromo
          setShowPromo={setShowPromo}
          templateIdToShow={templateIdToShow}
          availableTemplatesIds={availableTemplatesIds}
        />
      </AnimationContainer>
      <div className={`home-upload-sidebar-area`}>
        {optionsObj[currentOption]}
        <SideBarPromo
          currentOption={currentOption}
          swapFormFunction={swapFormFunction}
          collapsed={!currentOption}
        />
        <UploaderButtons
          {...{
            body,
            sideOptions,
            swapFormFunction,
            currentOption,
            hideJumbopay,
            handleSubmit,
          }}
        />
      </div>

      {/* on mobile its separate for now */}
      {mobileCondition && (
        <div className="home-upload-sidebar-options">
          <CloseIcon open={true} setCurrentOption={setCurrentOption} />
          <UploaderSideBarOptions
            {...{
              body,
              hideJumbopay,
              sideOptions,
              swapFormFunction,
              currentOption,
              handleSubmit,
            }}
          />
        </div>
      )}
      {captchaRequired ? <Recaptcha recaptchaRef={recaptchaRef} /> : null}
    </div>
  );
};
